import { z } from 'zod';
// SOT:ALERT_SCHEMAS — validates alert bodies on the server and shapes the alert tools Via uses.
const toolCallResultConditionSchema = z.object({
    source: z.literal('tool_call_result'),
    operation: z.literal('any-of'),
    toolIds: z.array(z.string().min(1)).min(1, 'Select at least one action'),
    toolNames: z.array(z.string()).optional()
});
const workflowErrorConditionSchema = z.object({
    source: z.literal('workflow_error'),
    operation: z.enum(['has_any_error', 'has_error_on_node']),
    nodeName: z.string().optional()
});
const sentimentConditionSchema = z.object({
    source: z.literal('sentiment'),
    operation: z.enum(['equals', 'not-equals', 'any-of']),
    value: z.union([z.string(), z.array(z.string())])
});
const qaResultConditionSchema = z.object({
    source: z.literal('qa_result'),
    operation: z.literal('equals'),
    value: z.enum(['pass', 'fail']),
    target: z.enum(['overall', 'rubric']),
    criterionId: z.string().optional(),
    criterionName: z.string().optional()
});
const agentConditionSchema = z.object({
    source: z.literal('agent'),
    operation: z.literal('any-of'),
    agentIds: z.array(z.string().min(1)).min(1, 'Select at least one agent'),
    agentNames: z.array(z.string()).optional()
});
const endedReasonConditionSchema = z.object({
    source: z.literal('ended_reason'),
    operation: z.enum(['equals', 'not-equals', 'any-of']),
    value: z.union([z.string(), z.array(z.string())])
});
const callErrorConditionSchema = z.object({
    source: z.literal('call_error')
});
const sessionVariableConditionSchema = z.object({
    source: z.literal('session_variable'),
    type: z.enum(['string', 'number', 'boolean', 'array']),
    objKey: z.string().min(1, 'Variable name is required'),
    operation: z.enum([
        'equals',
        'not-equals',
        'contains',
        'not-contains',
        'greater-than',
        'less-than',
        'greater-than-or-equal',
        'less-than-or-equal',
        'any-of',
        'none-of',
        'exists',
        'not-exists'
    ]),
    objValue: z.union([z.string(), z.number(), z.boolean(), z.array(z.string())])
});
const environmentConditionSchema = z.object({
    source: z.literal('environment'),
    operation: z.literal('any-of'),
    environmentIds: z.array(z.string().min(1)).min(1, 'Select at least one environment'),
    environmentNames: z.array(z.string()).optional()
});
const endedByConditionSchema = z.object({
    source: z.literal('ended_by'),
    operation: z.enum(['equals', 'any-of']),
    value: z.union([
        z.string().min(1, 'Select at least one value'),
        z.array(z.string().min(1)).min(1, 'Select at least one value')
    ])
});
const providerEventsConditionSchema = z.object({
    source: z.literal('provider_events'),
    component: z.enum(['voice', 'transcriber', 'model']),
    event: z.enum(['errors', 'fallbacks']),
    operation: z.enum([
        'equals',
        'not-equals',
        'greater-than',
        'less-than',
        'greater-than-or-equal',
        'less-than-or-equal'
    ]),
    value: z.number().int().min(0)
});
const toolCallResultValueConditionSchema = z.object({
    source: z.literal('tool_call_result_value'),
    toolIds: z.array(z.string().min(1)).optional(),
    toolNames: z.array(z.string()).optional(),
    field: z.enum(['result', 'error']),
    resultKey: z.string().optional(),
    type: z.enum(['string', 'number', 'boolean']),
    operation: z.enum([
        'equals',
        'not-equals',
        'contains',
        'not-contains',
        'greater-than',
        'less-than',
        'greater-than-or-equal',
        'less-than-or-equal',
        'exists',
        'not-exists'
    ]),
    objValue: z.union([z.string(), z.number(), z.boolean()])
});
export const alertConditionSchema = z.discriminatedUnion('source', [
    toolCallResultConditionSchema,
    workflowErrorConditionSchema,
    sentimentConditionSchema,
    qaResultConditionSchema,
    agentConditionSchema,
    endedReasonConditionSchema,
    callErrorConditionSchema,
    sessionVariableConditionSchema,
    environmentConditionSchema,
    endedByConditionSchema,
    providerEventsConditionSchema,
    toolCallResultValueConditionSchema
]);
export const slackRecipientSchema = z.object({
    workspaceId: z.string().min(1),
    channelId: z.string().min(1)
});
export const createAlertBodySchema = z.object({
    name: z.string({ error: 'Name is required' }).min(1, 'Name is required').max(255),
    description: z.string().max(1000).optional(),
    conditions: z
        .array(z.array(alertConditionSchema).min(1, 'Each condition group must have at least one condition'))
        .min(1, 'At least one condition group is required'),
    recipients: z.array(z.string().trim().toLowerCase().pipe(z.string().email())).default([]),
    slackRecipients: z.array(slackRecipientSchema).default([]),
    lookbackMs: z.number().int().min(0).nullable().optional(),
    emailSubjectTemplate: z.string().max(500).nullable().optional(),
    emailBodyTemplate: z.string().max(5000).nullable().optional()
});
export const updateAlertBodySchema = z.object({
    name: z.string().min(1).max(255).optional(),
    description: z.string().max(1000).nullable().optional(),
    conditions: z
        .array(z.array(alertConditionSchema).min(1, 'Each condition group must have at least one condition'))
        .min(1, 'At least one condition group is required')
        .optional(),
    enabledAt: z.number().int().nullable().optional(),
    recipients: z.array(z.string().trim().toLowerCase().pipe(z.string().email())).optional(),
    slackRecipients: z.array(slackRecipientSchema).optional(),
    snoozedUntil: z.number().int().nullable().optional(),
    lookbackMs: z.number().int().min(0).nullable().optional(),
    emailSubjectTemplate: z.string().max(500).nullable().optional(),
    emailBodyTemplate: z.string().max(5000).nullable().optional()
});
//# sourceMappingURL=schemas.js.map